import {
  PROMOTION_SCHEDULE_ADMIN_UNASSIGNED,
  PROMOTION_SCHEDULE_STATUS,
  PROMOTION_SCHEDULE_TRACK,
  PROMOTION_SCHEDULE_TYPE,
  PromotionScheduleStatus,
  PromotionScheduleTrack,
  PromotionScheduleType,
} from '../../../../lib/api/promotionSchedule';

export const PROMOTION_SCHEDULE_PAGE_NAME = '프로모션 타임라인';

/** 리스트·상세 페이지명 (AMZUWEB-682) — 표면 용어는 `프로모션` 으로 통일한다 */
export const PROMOTION_SCHEDULE_LIST_PAGE_NAME = '프로모션 리스트';
export const PROMOTION_SCHEDULE_DETAIL_PAGE_NAME = '프로모션 상세';

type TrackMeta = {
  label: string;
  /** 간트 밴드 부제로 보여줄 운영 규칙 문구 */
  description?: string;
  /**
   * 주당 최소 개수. 이 값이 있는 트랙만 미달 경고를 계산한다.
   *
   * `description` 문자열("주 3-4개 이상")에서 파싱하지 않고 별도 값으로 둔다 —
   * 표기는 운영이 바꿀 수 있는 문구고, 판정 기준은 코드가 읽어야 하는 수치라 성격이 다르다.
   * 범위 표기("3-4개")의 기준은 **하한**이다.
   */
  minPerWeek?: number;
  /** 트랙 대표색 — 밴드 라벨·캘린더가 쓴다. 원색 그대로다. */
  color: string;
  /**
   * 간트 막대 전용 톤.
   *
   * 막대를 원색으로 꽉 채우면 수십 개가 깔릴 때 화면 절반이 고채도가 되어 눈이 피로하다.
   * 게다가 `#ec4899` 위의 흰 글씨는 대비가 3.1:1 로 **WCAG AA(4.5:1) 미달**이다.
   * 그래서 막대는 **연한 바탕 + 어두운 글자**로 두고, 트랙 색은 좌측 4px 스트라이프가 맡는다.
   *
   * `color` 와 분리한 이유 — 캘린더는 칩이 작아 원색이 오히려 잘 읽힌다. 화면마다 필요가 다르다.
   */
  barTone: BarTone;
};

/** 간트 막대 한 벌의 색. hue 는 `color` 와 같고 채도·명도만 다르다. */
export type BarTone = {
  /** 좌측 스트라이프 — 트랙을 구분하는 유일한 색면 */
  stripe: string;
  bg: string;
  border: string;
  /**
   * 캘린더 막대 테두리.
   *
   * 캘린더는 막대 배경이 흰색이라 트랙을 알리는 색면이 좌측 5px 스트라이프뿐이다.
   * 막대가 한 주에 10행씩 쌓이면 그 5px 도 잘 안 잡혀서, 테두리에 트랙색을 실어
   * 윤곽 전체가 트랙을 말하게 한다. `border` 보다 진하고 `stripe` 보다는 연하다.
   * 배경은 흰색 그대로다 — 색 면적을 줄인다는 원칙은 유지한다.
   */
  barBorder: string;
  /** 글자·아이콘 (바탕과 7:1 이상) */
  text: string;
};

/**
 * 트랙 메타 — 간트 밴드의 표시 정보.
 *
 * `description`·`minPerWeek` 은 기존 구글시트 라벨에 박혀 있던 운영 규칙을 승계한 것이다
 * (예: "일반 기획전 — 상품 연합 (*주 3-4개 이상)"). 규칙의 주인은 운영이고 바뀔 수 있어
 * DB 가 아닌 상수로 둔다.
 */
export const TRACK_META: Record<PromotionScheduleTrack, TrackMeta> = {
  [PROMOTION_SCHEDULE_TRACK.PROMOTION]: {
    label: '프로모션/이벤트',
    color: '#6366f1',
    barTone: {
      stripe: '#494bca',
      bg: '#d6d6f5',
      border: '#9698e3',
      barBorder: '#7b7dda',
      text: '#2a2c79',
    },
  },
  [PROMOTION_SCHEDULE_TRACK.FLASH_DEAL]: {
    label: '반짝딜',
    color: '#f59e0b',
    barTone: {
      stripe: '#ca9b49',
      bg: '#f5ead6',
      border: '#e3c796',
      barBorder: '#dab87b',
      text: '#795c2a',
    },
  },
  [PROMOTION_SCHEDULE_TRACK.REGULAR_WEEKLY]: {
    label: '정기 기획전 (주차별)',
    color: '#10b981',
    barTone: {
      stripe: '#49ca9f',
      bg: '#d6f5eb',
      border: '#96e3ca',
      barBorder: '#7bdabb',
      text: '#2a795f',
    },
  },
  [PROMOTION_SCHEDULE_TRACK.REGULAR_MONTHLY]: {
    label: '정기 기획전 (월별)',
    color: '#14b8a6',
    barTone: {
      stripe: '#49cabb',
      bg: '#d6f5f2',
      border: '#96e3da',
      barBorder: '#7bdacf',
      text: '#29756c',
    },
  },
  [PROMOTION_SCHEDULE_TRACK.PRODUCT_UNION]: {
    label: '일반 기획전 (상품 연합)',
    description: '주 3-4개 이상',
    minPerWeek: 3,
    color: '#ec4899',
    barTone: {
      stripe: '#ca498a',
      bg: '#f5d6e6',
      border: '#e396bd',
      barBorder: '#da7bac',
      text: '#792a52',
    },
  },
  [PROMOTION_SCHEDULE_TRACK.BRAND_UNIT]: {
    label: '브랜드 단위',
    description: '주 5개 이상',
    minPerWeek: 5,
    color: '#8b5cf6',
    barTone: {
      stripe: '#7049ca',
      bg: '#dfd6f5',
      border: '#ad96e3',
      barBorder: '#977bda',
      text: '#422a79',
    },
  },
};

/** 간트 밴드 표시 순서 — 구글시트의 행 순서를 따른다. */
export const TRACK_ORDER: PromotionScheduleTrack[] = [
  PROMOTION_SCHEDULE_TRACK.PROMOTION,
  PROMOTION_SCHEDULE_TRACK.FLASH_DEAL,
  PROMOTION_SCHEDULE_TRACK.REGULAR_WEEKLY,
  PROMOTION_SCHEDULE_TRACK.REGULAR_MONTHLY,
  PROMOTION_SCHEDULE_TRACK.PRODUCT_UNION,
  PROMOTION_SCHEDULE_TRACK.BRAND_UNIT,
];

export const TYPE_LABEL: Record<PromotionScheduleType, string> = {
  [PROMOTION_SCHEDULE_TYPE.BRAND_UNION]: '브랜드 연합',
  [PROMOTION_SCHEDULE_TYPE.PRODUCT_UNION]: '상품연합',
  [PROMOTION_SCHEDULE_TYPE.BRAND_SOLO]: '브랜드 단독',
  [PROMOTION_SCHEDULE_TYPE.NEW_PRODUCT]: '신상품',
  [PROMOTION_SCHEDULE_TYPE.LARGE_PROMOTION]: '대형프로모션',
  [PROMOTION_SCHEDULE_TYPE.TIME_DEAL]: '타임딜',
};

/** 쿠폰 할인 타입 — 쿠폰 도메인 규약(0: 금액, 1: %) */
export const COUPON_DISCOUNT_TYPE = {
  AMOUNT: 0,
  RATE: 1,
} as const;

export const STATUS_LABEL: Record<PromotionScheduleStatus, string> = {
  [PROMOTION_SCHEDULE_STATUS.PLANNED]: '미확정',
  [PROMOTION_SCHEDULE_STATUS.CONFIRMED]: '확정',
  [PROMOTION_SCHEDULE_STATUS.CANCELED]: '취소',
};

/**
 * 상태별 막대 표현.
 * 미확정을 회색으로 두는 것은 구글시트의 "확정 전까지는 회색 처리" 관례를 그대로 승계한 것 —
 * 운영이 보던 시각 언어를 유지해 학습 비용을 없앤다.
 */
export const STATUS_STYLE: Record<PromotionScheduleStatus, { opacity: number; isGray: boolean }> = {
  [PROMOTION_SCHEDULE_STATUS.PLANNED]: { opacity: 1, isGray: true },
  [PROMOTION_SCHEDULE_STATUS.CONFIRMED]: { opacity: 1, isGray: false },
  [PROMOTION_SCHEDULE_STATUS.CANCELED]: { opacity: 0.45, isGray: true },
};

/**
 * 미확정 항목의 막대 톤.
 *
 * 채도를 낮추지 않는다 — 다른 트랙이 연해진 만큼 **확정/미확정 대비는 오히려 살아나야** 한다.
 * 회색은 원래 채도가 없어 눈을 피로하게 하지도 않는다.
 */
export const GRAY_BAR_TONE: BarTone = {
  stripe: '#9297a0',
  bg: '#e9eaec',
  border: '#c3c6cb',
  barBorder: '#b2b5bc',
  text: '#5b5f66',
};

export const VIEW_MODE = {
  GANTT: 'gantt',
  CALENDAR: 'calendar',
} as const;

export type ViewMode = (typeof VIEW_MODE)[keyof typeof VIEW_MODE];

/**
 * 조회 기간 선택 단위.
 * 연간 계획을 보는 화면이라 일 단위만으로는 범위를 잡기 번거롭다 —
 * 월(기본)·연 단위로 한 번에 고를 수 있게 한다.
 */
export const PERIOD_UNIT = {
  DAY: 'day',
  MONTH: 'month',
  YEAR: 'year',
} as const;

export type PeriodUnit = (typeof PERIOD_UNIT)[keyof typeof PERIOD_UNIT];

export const PERIOD_UNIT_LABEL: Record<PeriodUnit, string> = {
  [PERIOD_UNIT.DAY]: '일',
  [PERIOD_UNIT.MONTH]: '월',
  [PERIOD_UNIT.YEAR]: '연',
};

/** antd RangePicker 의 `picker` prop 과 1:1 대응. */
export const PERIOD_UNIT_PICKER: Record<PeriodUnit, 'date' | 'month' | 'year'> = {
  [PERIOD_UNIT.DAY]: 'date',
  [PERIOD_UNIT.MONTH]: 'month',
  [PERIOD_UNIT.YEAR]: 'year',
};

/**
 * 담당자 필터의 "미지정" 옵션 값.
 *
 * 값을 여기 다시 쓰지 않고 **API 계약의 센티널을 그대로 재수출**한다 —
 * 백엔드가 정한 규약이라 두 곳에 각자 적어두면 그쪽이 바뀔 때 조용히 어긋난다.
 */
export const MANAGER_UNASSIGNED_VALUE = PROMOTION_SCHEDULE_ADMIN_UNASSIGNED;

/**
 * 기획전 노출 상태 — 스케줄 API 가 주는 DB 원값.
 *
 * 목록 API 는 문자열(`'unlimited'`)로 주므로 `fetchEventOptions` 에서 이 숫자로 맞춘다.
 * `UNLIMITED` 는 **기간이 있어도 끝나지 않는** 상시 노출이라, 날짜가 채워져 있어도
 * 기간 판정에서 빼야 한다.
 */
export const EVENT_STATUS = {
  INACTIVE: 0,
  ACTIVE: 1,
  UNLIMITED: 2,
} as const;
